/*
 * LOCAL DRAFT ONLY — NOT DEPLOYED.
 *
 * Keeps the legitimate acquisition source across the external MitID age-check
 * redirect. This is an analytics/reporting aid only; it must never be used for
 * access control or for deciding whether a customer is old enough to buy.
 *
 * Attribution is persisted only after the Shopify Customer Privacy consent API
 * has loaded and granted the matching purpose: analytics for organic/referral
 * traffic and marketing for campaign parameters/click IDs. If the API cannot
 * be loaded or its methods are unavailable, tracking fails closed.
 */
(function () {
  'use strict';

  var CALLBACK_PATH = '/a/eid/complete';
  var STORAGE_KEY = 'devinos_attribution_repair_v1';
  var MAX_AGE_MS = 2 * 60 * 60 * 1000;
  var TRACKING_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
    'gclid', 'fbclid', 'msclkid', 'ttclid', 'sclid',
    'meta_campaign_id', 'meta_adset_id', 'meta_ad_id', 'meta_placement', 'meta_platform'
  ];
  var CLICK_ID_KEYS = ['gclid', 'fbclid', 'msclkid', 'ttclid', 'sclid'];
  var META_MARKETING_KEYS = ['meta_campaign_id', 'meta_adset_id', 'meta_ad_id', 'meta_placement', 'meta_platform'];
  var SENSITIVE_KEYS = [
    'code', 'state', 'error', 'error_description',
    'access_token', 'id_token', 'refresh_token', 'token', 'session', 'session_token', 'authorization'
  ];
  var MARKETING_MEDIUMS = /^(cpc|ppc|paid|paid_social|social|social_paid|display|affiliate|influencer|email|sms|newsletter|retargeting|remarketing|video|shopping|cpm|cpv|cpp)$/i;
  var MITID_HOST = /(^|\.)(mitid|e-id|eid|age[-_]?check|alders[-_]?kontrol)(\.[a-z0-9-]+)+$/i;
  var consentApiState = 'unrequested';
  var consentApiCallbacks = [];

  function storage() {
    try {
      return window.sessionStorage;
    } catch (error) {
      try { return window.localStorage; } catch (ignored) { return null; }
    }
  }

  function finishConsentApi(ready) {
    var callbacks = consentApiCallbacks.slice();
    consentApiCallbacks = [];
    consentApiState = ready ? 'ready' : 'failed';
    callbacks.forEach(function (callback) { callback(ready); });
  }

  function hasConsentApi() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    return Boolean(
      privacy &&
      typeof privacy.analyticsProcessingAllowed === 'function' &&
      typeof privacy.marketingAllowed === 'function'
    );
  }

  function ensureConsentApi(callback) {
    if (consentApiState === 'ready') {
      callback(true);
      return;
    }

    // A theme or app can have loaded the documented API before this asset.
    // In that valid runtime there is no reason to require loadFeatures again.
    if (hasConsentApi()) {
      consentApiState = 'ready';
      callback(true);
      return;
    }

    var shopify = window.Shopify;
    if (consentApiState === 'failed' && (!shopify || typeof shopify.loadFeatures !== 'function')) {
      callback(false);
      return;
    }

    // An earlier attempt may have run before Shopify exposed the loader. A
    // later official privacy-API event is allowed to retry; until then this
    // remains fail-closed.
    if (consentApiState === 'failed') consentApiState = 'unrequested';

    consentApiCallbacks.push(callback);
    if (consentApiState === 'loading') return;
    consentApiState = 'loading';

    if (!shopify || typeof shopify.loadFeatures !== 'function') {
      finishConsentApi(false);
      return;
    }

    try {
      // Shopify's documented Customer Privacy feature loader. Do not infer
      // consent from cookies or grant consent from this script.
      shopify.loadFeatures([
        { name: 'consent-tracking-api', version: '0.1' }
      ], function (error) {
        finishConsentApi(!error && hasConsentApi());
      });
    } catch (error) {
      finishConsentApi(false);
    }
  }

  function consentState() {
    if (consentApiState !== 'ready' || !hasConsentApi()) {
      return { analytics: false, marketing: false };
    }
    try {
      var privacy = window.Shopify.customerPrivacy;
      return {
        analytics: privacy.analyticsProcessingAllowed() === true,
        marketing: privacy.marketingAllowed() === true
      };
    } catch (error) {
      return { analytics: false, marketing: false };
    }
  }

  function isMarketingTracking(tracking, explicitCampaign) {
    return CLICK_ID_KEYS.some(function (key) { return Boolean(tracking[key]); }) ||
      META_MARKETING_KEYS.some(function (key) { return Boolean(tracking[key]); }) ||
      Boolean(explicitCampaign) ||
      MARKETING_MEDIUMS.test(String(tracking.utm_medium || ''));
  }

  function trackingAllowed(tracking, explicitCampaign) {
    var state = consentState();
    return isMarketingTracking(tracking, explicitCampaign) ? state.marketing : state.analytics;
  }

  function sameSite(url) {
    try {
      var host = new URL(url).hostname.toLowerCase();
      var current = window.location.hostname.toLowerCase();
      return host === current || host === 'www.' + current || host.endsWith('.devinosshop.shop');
    } catch (error) {
      return false;
    }
  }

  function classifyReferrer(referrer) {
    if (!referrer || sameSite(referrer)) return null;
    try {
      var host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
      if (MITID_HOST.test(host)) return null;
      if (/google\./.test(host)) return { source: 'google', medium: 'organic' };
      if (/bing\./.test(host)) return { source: 'bing', medium: 'organic' };
      if (/duckduckgo\./.test(host)) return { source: 'duckduckgo', medium: 'organic' };
      if (/(^|\.)facebook\.com$|(^|\.)instagram\.com$|(^|\.)l\.facebook\.com$/.test(host)) {
        return { source: host.indexOf('instagram') >= 0 ? 'instagram' : 'facebook', medium: 'social' };
      }
      if (/(^|\.)tiktok\.com$/.test(host)) return { source: 'tiktok', medium: 'social' };
      return { source: host, medium: 'referral' };
    } catch (error) {
      return null;
    }
  }

  function cleanValue(value) {
    if (!value) return '';
    return String(value).replace(/[\u0000-\u001f\u007f]/g, '').slice(0, 256);
  }

  function removeKeys(url, keys) {
    keys.forEach(function (key) { url.searchParams.delete(key); });
  }

  function safeReturnUrl(value) {
    if (typeof value !== 'string' || !value) return null;
    try {
      var url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || url.pathname === CALLBACK_PATH) return null;
      // Tracking is restored only after current consent is checked. OAuth and
      // session material are never kept in the attribution record.
      removeKeys(url, TRACKING_KEYS);
      removeKeys(url, SENSITIVE_KEYS);
      return url.pathname + url.search + url.hash;
    } catch (error) {
      return null;
    }
  }

  function sanitizedTracking(value) {
    var source = value && typeof value === 'object' ? value : {};
    var result = {};
    TRACKING_KEYS.forEach(function (key) {
      var safe = cleanValue(source[key]);
      if (safe) result[key] = safe;
    });
    return result;
  }

  function normalizeSavedValue(value) {
    if (!value || typeof value !== 'object' || !Number.isFinite(value.savedAt) || Date.now() - value.savedAt >= MAX_AGE_MS) {
      return null;
    }
    var returnUrl = safeReturnUrl(value.returnUrl);
    var tracking = sanitizedTracking(value.tracking);
    if (!returnUrl || !Object.keys(tracking).length) return null;
    return {
      savedAt: value.savedAt,
      returnUrl: returnUrl,
      tracking: tracking,
      // Older records stored this flag. Retain its stricter marketing gate,
      // while meta IDs/click IDs still require marketing when it is absent.
      marketing: value.marketing === true || isMarketingTracking(tracking, false)
    };
  }

  function read() {
    var store = storage();
    if (!store) return null;
    try {
      return normalizeSavedValue(JSON.parse(store.getItem(STORAGE_KEY) || 'null'));
    } catch (error) {
      return null;
    }
  }

  function write(value) {
    var store = storage();
    if (!store) return;
    try { store.setItem(STORAGE_KEY, JSON.stringify(value)); } catch (error) {}
  }

  function clear() {
    var store = storage();
    if (!store) return;
    try { store.removeItem(STORAGE_KEY); } catch (error) {}
  }

  function currentUrl() {
    return safeReturnUrl(window.location.href);
  }

  function capture() {
    var params = new URLSearchParams(window.location.search);
    var hasTracking = TRACKING_KEYS.some(function (key) { return params.has(key); });
    var explicitCampaign = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
      .some(function (key) { return params.has(key); });
    var source = params.get('utm_source');
    var medium = params.get('utm_medium');
    var inferred = classifyReferrer(document.referrer);
    var existing = read();

    if (existing) {
      // A change from grant to revoke must not leave the first-touch record
      // available for a later redirect.
      if (!trackingAllowed(existing.tracking, existing.marketing)) {
        clear();
        return;
      }
      existing.returnUrl = currentUrl();
      existing.savedAt = Date.now();
      if (existing.returnUrl) write(existing); else clear();
      return;
    }

    if (!source && inferred) source = inferred.source;
    if (!medium && inferred) medium = inferred.medium;
    if (!hasTracking && !inferred) return;

    var tracking = {};
    TRACKING_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) tracking[key] = value;
    });
    if (source && !tracking.utm_source) tracking.utm_source = source;
    if (medium && !tracking.utm_medium) tracking.utm_medium = medium;

    var safeTracking = sanitizedTracking(tracking);
    if (!Object.keys(safeTracking).length || !trackingAllowed(safeTracking, explicitCampaign)) return;

    write({
      savedAt: Date.now(),
      returnUrl: currentUrl(),
      tracking: safeTracking,
      marketing: isMarketingTracking(safeTracking, explicitCampaign)
    });
  }

  function repairCallback() {
    var value = read();
    if (!value || !value.returnUrl) return;

    var target;
    try {
      target = new URL(value.returnUrl, window.location.origin);
    } catch (error) {
      clear();
      return;
    }
    if (target.origin !== window.location.origin || target.pathname === CALLBACK_PATH) {
      clear();
      return;
    }

    // `returnUrl` was stripped on both capture and read, including values
    // written by older versions. Put only whitelisted attribution back, and
    // only if current consent still allows its purpose.
    if (!new URLSearchParams(window.location.search).has('mitid_error') && trackingAllowed(value.tracking, value.marketing)) {
      Object.keys(value.tracking).forEach(function (key) {
        if (!target.searchParams.has(key)) target.searchParams.set(key, value.tracking[key]);
      });
    }

    clear();
    window.location.replace(target.toString());
  }

  function run() {
    ensureConsentApi(function () {
      if (window.location.pathname === CALLBACK_PATH) {
        // Let the MitID app finish setting its verification state first.
        window.setTimeout(repairCallback, 200);
      } else {
        capture();
      }
    });
  }

  document.addEventListener('visitorConsentCollected', run);
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', run);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
